/**
 * Hygraph query documents, shared by the pages (server-side render) and by
 * server-middleware/hygraph.js (the browser-facing proxy).
 *
 * They live here rather than inline in asyncData for one reason: the browser is
 * no longer allowed to send Hygraph arbitrary GraphQL. The credential that talks
 * to Hygraph can create, update and delete content, so it stays on the server
 * and the client may only ask for an operation NAME from this file. Adding a new
 * blog query means adding it here, not building a string in a component.
 *
 * Stage and slug are real GraphQL variables, not interpolated text. Besides
 * being what the proxy needs, this closes an injection hole: the article query
 * used to build `where: { slug: "${slug}" }` from a URL segment, so a slug
 * containing a double quote could rewrite the query.
 */

// Selection every image asset needs for the responsive srcset builder
// (see utils/hygraphImage.js — it requires width/height/size/mimeType).
const ASSET = "url width height size mimeType";

// `first` is bounded here as well as in the proxy, so a server-side caller
// cannot ask Hygraph to hand back the whole collection in one request.
const clampFirst = (first, fallback) =>
  Number.isInteger(first) && first >= 1 && first <= 100 ? first : fallback;

export const OPERATIONS = {
  // Blog listing. `draftsOnly` drives the /blog-draft-preview variant, which
  // hides already-published articles from the review queue.
  "blog:list": {
    // The DRAFT stage is reachable only from the server — see PUBLIC_OPERATIONS.
    document: ({ draftsOnly }) => `
      query BlogList($stage: Stage!, $first: Int!) {
        articles(
          stage: $stage
          ${draftsOnly ? "where: { documentInStages_none: { stage: PUBLISHED } }" : ""}
          orderBy: articlePublishedAt_DESC
          first: $first
        ) {
          slug
          title
          deck
          readingTime
          articlePublishedAt
          pillar
          heroAlt
          heroImage { ${ASSET} }
        }
      }`,
    // `first` used to be hardcoded to 24 here, silently ignoring whatever the
    // caller asked for — a trap for anyone adding blog pagination. It is now
    // honoured, clamped to the same 1..100 range the proxy enforces.
    variables: ({ stage, first }) => ({ stage, first: clampFirst(first, 24) }),
  },

  // Single article + the site settings block rendered beneath it.
  "blog:article": {
    document: () => `
      query BlogArticle($stage: Stage!, $slug: String!) {
        articles(stage: $stage, where: { slug: $slug }) {
          title
          slug
          deck
          pillar
          pillarLabel
          readingTime
          articlePublishedAt
          articleUpdatedAt
          seoTitle
          seoDescription
          author {
            name
            role
            initials
            shortBio
            linkedInUrl
            avatar { ${ASSET} }
          }
          heroImage { ${ASSET} }
          socialShareImage { url }
          heroAlt
          tldrItems
          answerCapsule
          tocItems(first: 100) { number label anchor }
          faqItems(first: 100) { question answer }
          body(first: 100) {
            __typename
            ... on ProseBlock { content { html } }
            ... on H2Block { eyebrowLabel heading anchor }
            ... on ImageBlock { image { ${ASSET} } alt caption aspectRatio width }
            ... on Blockquote { bqHeading: heading body }
            ... on ImagePair { leftImage { ${ASSET} } leftAlt rightImage { ${ASSET} } rightAlt pairCaption: caption }
            ... on VideoEmbed { videoId title caption thumbnailOverride { ${ASSET} } vidAspectRatio: aspectRatio }
            ... on DataTable { headers rows caption }
            ... on MidCTA { headline body buttonLabel buttonUrl buttonStyle }
          }
          bottomCtaHeadline
          bottomCtaBody
          bottomCtaPrimaryLabel
          bottomCtaPrimaryUrl
          bottomCtaSecondaryLabel
          bottomCtaSecondaryUrl
          furtherReading(first: 100) { slug title }
        }
        # Site chrome (logo, about blurb, share image) is not part of the article
        # being reviewed, so it must NOT follow $stage. Pinning it to $stage made
        # /blog-draft-preview ask for siteSettingsItems in DRAFT, which the CMS
        # token has no grant for — one denied field failed the whole query and
        # the preview rendered the error page instead of the draft.
        siteSettingsItems(first: 1, stage: PUBLISHED) {
          logo { ${ASSET} }
          aboutEyebrowLabel
          aboutBlurb
          defaultShareImage { url }
        }
      }`,
    variables: ({ stage, slug }) => ({ stage, slug }),
  },
};

// ── Legacy blog (pages/legacy-blog) ───────────────────────────────────────────
// `filter` is a boolean field name used as a category toggle; it is matched
// against a fixed list rather than interpolated, since it names a schema field
// and so cannot be a GraphQL variable.
const LEGACY_FILTERS = ["expertTipsGuides", "customerStories"];
const legacyWhere = (filter) =>
  LEGACY_FILTERS.includes(filter) ? `where: { ${filter}: true }` : "";

Object.assign(OPERATIONS, {
  "legacy:list": {
    document: ({ filter }) => `
      query LegacyList($first: Int!, $skip: Int!) {
        posts(first: $first, skip: $skip, ${legacyWhere(filter)} orderBy: createdAt_DESC) {
          expertTipsGuides
          customerStories
          slug
          title
          description
          poster { ${ASSET} }
        }
      }`,
    variables: ({ first, skip }) => ({ first, skip: skip || 0 }),
  },

  // `aggregate { count }` is the total matching the filter. This used to read
  // `pageInfo { pageSize }`, which is the size of the CURRENT page — capped by
  // Hygraph's default `first` of 10 — so the pager reported 2 pages no matter
  // how many posts existed. It only looked right because there are exactly 10
  // posts today (pageSize=10, count=10 coincide).
  "legacy:count": {
    document: ({ filter }) => `
      query LegacyCount {
        postsConnection${legacyWhere(filter) ? `(${legacyWhere(filter)})` : ""} {
          aggregate { count }
        }
      }`,
    variables: () => ({}),
  },

  "legacy:post": {
    document: () => `
      query LegacyPost($slug: String!) {
        posts(where: { slug: $slug }) {
          title
          description
          poster { ${ASSET} }
          contentTitle
          content { html }
          bottomSectionTitle
          bottomSectionDescription
          bottomSectionLink
        }
      }`,
    variables: ({ slug }) => ({ slug }),
  },

  // ── Dealer changelog (pages/dealer/changelog) ──────────────────────────────
  // Behind the auth middleware in the UI, but the content itself is published
  // release notes, so the proxy treats it like any other published read.
  "changelog:list": {
    document: () => `
      query ChangeLogList($first: Int!, $skip: Int!) {
        changeLogs(first: $first, skip: $skip, orderBy: dateOfChange_DESC) {
          changeLog
          dateOfChange
          link
        }
      }`,
    variables: ({ first, skip }) => ({ first, skip: skip || 0 }),
  },

  "changelog:count": {
    document: () => `
      query ChangeLogCount {
        changeLogsConnection { aggregate { count } }
      }`,
    variables: () => ({}),
  },

  // ── Dealer file pages (pages/dealer/downloads, pages/dealer/information) ───
  // One operation for both: the pages differ only in which DownloadPage slug
  // they ask for ("dealer-downloads" / "dealer-information") and in how they
  // group the sections client-side. The selection is the union of what both
  // templates read, so neither page needs a query of its own.
  "dealer:page": {
    document: () => `
      query DealerPage($slug: String!) {
        downloadPage(where: { slug: $slug }) {
          id
          title
          slug
          updatedAt
          downloadSection {
            id
            title
            downloadBlock(orderBy: order_ASC) {
              id
              title
              order
              downloadFile {
                id
                title
                file { id url fileName size mimeType }
              }
            }
          }
        }
      }`,
    variables: ({ slug }) => ({ slug }),
  },
});

// Operations the browser may request through the proxy. Every one reads
// published content only — the proxy pins stage to PUBLISHED and ignores any
// stage in the request, so DRAFT is reachable solely during server-side
// rendering of /blog-draft-preview, behind the existing dealer-admin guard.
// The only params a browser may influence. `stage` and `draftsOnly` are absent
// on purpose — the proxy pins those itself.
//
// This list is the contract shared by mixins/hygraph.js (which refuses to send
// anything outside it) and server-middleware/hygraph.js (which rejects a payload
// carrying anything outside it). It exists because the mixin used to forward a
// hand-written subset: a new operation taking a param nobody had added there got
// it silently dropped on client-side navigation, and the page just rendered the
// wrong data. Adding a param now means adding it here AND validating it in the
// proxy — or getting a loud error.
export const PROXY_PARAMS = ["slug", "first", "skip", "filter"];

export const PUBLIC_OPERATIONS = [
  "blog:list",
  "blog:article",
  "legacy:list",
  "legacy:count",
  "legacy:post",
  "changelog:list",
  "changelog:count",
  "dealer:page",
];

export function buildOperation(name, params = {}) {
  const op = OPERATIONS[name];
  if (!op) throw new Error(`Unknown Hygraph operation: ${name}`);
  return {
    query: op.document(params),
    variables: op.variables(params),
  };
}
